import { useEffect, useMemo, useState } from "react";
import { Activity, Heart, Brain, Pencil } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";

/**
 * Biorhythm mini-panel for the "Today" tab.
 *
 * Classic three-cycle theory:
 *   • Physical      — 23-day sine
 *   • Emotional     — 28-day sine
 *   • Intellectual  — 33-day sine
 * Values are +/- 100. Positive = up-cycle, negative = down-cycle,
 * near zero = a "critical" day (be gentle).
 *
 * DOB is stored in localStorage (no auth, no server), so returning
 * visitors see the panel populated instantly.
 */
const STORAGE_KEY = "ep_biorhythm_dob";
const CYCLES = [
  { name: "Physical",     days: 23, colour: "#e5a87c", Icon: Activity, testid: "biorhythm-physical" },
  { name: "Emotional",    days: 28, colour: "#d4a24c", Icon: Heart,    testid: "biorhythm-emotional" },
  { name: "Intellectual", days: 33, colour: "#c5b6e7", Icon: Brain,    testid: "biorhythm-intellectual" },
];
const WINDOW_DAYS = 30; // 15 back, 15 forward

function daysSince(dob) {
  const dobD = new Date(dob + "T00:00:00Z");
  const now = new Date();
  const nowD = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate()));
  return Math.floor((nowD - dobD) / 86400000);
}

function valueAt(days, cycleDays) {
  return Math.sin((2 * Math.PI * days) / cycleDays);
}

function reading(pct) {
  const abs = Math.abs(pct);
  if (abs < 10) return "critical day — be gentle";
  if (pct > 75) return "peak — ride the wave";
  if (pct > 30) return "on the rise";
  if (pct > -10) return "steady";
  if (pct > -50) return "resting";
  return "deep dip — protect your energy";
}

export default function BiorhythmPanel() {
  const [dob, setDob] = useState("");
  const [showEditor, setShowEditor] = useState(false);

  useEffect(() => {
    const saved = typeof window !== "undefined" ? localStorage.getItem(STORAGE_KEY) : null;
    if (saved) setDob(saved);
    else setShowEditor(true);
  }, []);

  const save = (e) => {
    e?.preventDefault();
    if (!dob) return;
    try { localStorage.setItem(STORAGE_KEY, dob); } catch { /* ignore quota */ }
    setShowEditor(false);
  };

  const data = useMemo(() => {
    if (!dob) return null;
    const total = daysSince(dob);
    const today = CYCLES.map((c) => ({
      ...c,
      pct: Math.round(valueAt(total, c.days) * 100),
    }));
    // Curve points across the 30-day window
    const points = CYCLES.map((c) => {
      const pts = [];
      for (let d = -WINDOW_DAYS / 2; d <= WINDOW_DAYS / 2; d += 0.5) {
        pts.push({ x: d, y: valueAt(total + d, c.days) });
      }
      return { ...c, pts };
    });
    return { today, points };
  }, [dob]);

  // ── Editor state ────
  if (showEditor || !dob) {
    return (
      <div className="parchment-card rounded-2xl p-6 md:p-8 hover-lift" data-testid="biorhythm-panel">
        <div className="flex items-center gap-3 mb-4">
          <Activity size={16} className="text-[color:var(--gold)]" />
          <div className="label-overline">Biorhythm · a small daily curve</div>
        </div>
        <p className="font-serif-italic text-[color:var(--text-secondary)] mb-5 text-sm">
          Enter your date of birth (kept on this device only) and we&apos;ll draw your Physical, Emotional, and Intellectual cycles for the next fortnight.
        </p>
        <form onSubmit={save} className="flex flex-col sm:flex-row items-stretch gap-3" data-testid="biorhythm-form">
          <Input
            type="date"
            value={dob}
            onChange={(e) => setDob(e.target.value)}
            className="bg-white border-slate-300 rounded-lg h-11 text-slate-900 font-sans-body flex-1"
            required
            data-testid="biorhythm-dob-input"
          />
          <Button
            type="submit"
            className="h-11 rounded-full bg-[color:var(--gold)] hover:bg-[color:var(--gold-soft)] text-[color:var(--bg-base)] font-medium"
            data-testid="biorhythm-save"
          >
            Draw my rhythm
          </Button>
        </form>
      </div>
    );
  }

  const width = 560;
  const height = 160;
  const midY = height / 2;
  const px = (day) => ((day + WINDOW_DAYS / 2) / WINDOW_DAYS) * width;
  const py = (val) => midY - val * (midY - 12);

  return (
    <div className="parchment-card rounded-2xl p-6 md:p-8 hover-lift" data-testid="biorhythm-panel">
      <div className="flex items-start justify-between gap-4 mb-6">
        <div>
          <div className="label-overline constellation-underline">Biorhythm · your cycles today</div>
          <p className="mt-2 font-serif-italic text-[color:var(--text-secondary)] text-sm">
            Physical (23d) · Emotional (28d) · Intellectual (33d) — sine cycles from your birth date.
          </p>
        </div>
        <button
          onClick={() => setShowEditor(true)}
          className="inline-flex items-center gap-1 label-overline hover:text-[color:var(--parchment)] transition"
          data-testid="biorhythm-edit"
        >
          <Pencil size={11} /> edit
        </button>
      </div>

      {/* Today's three stats */}
      <div className="grid grid-cols-3 gap-3 md:gap-4 mb-6">
        {data.today.map((c) => (
          <div key={c.name} className="rounded-xl border border-[color:var(--gold)]/15 p-3" data-testid={c.testid}>
            <div className="flex items-center gap-2">
              <c.Icon size={13} style={{ color: c.colour }} />
              <span className="label-overline text-[10px]">{c.name}</span>
            </div>
            <div
              className="mt-2 font-serif-display text-[color:var(--parchment)] text-2xl md:text-3xl leading-tight"
              style={{ color: c.pct >= 0 ? c.colour : undefined }}
            >
              {c.pct > 0 ? "+" : ""}{c.pct}
            </div>
            <div className="font-serif-italic text-[color:var(--text-muted)] text-xs mt-1">
              {reading(c.pct)}
            </div>
          </div>
        ))}
      </div>

      {/* SVG curves */}
      <svg viewBox={`0 0 ${width} ${height}`} className="w-full h-auto" aria-hidden="true">
        {/* zero line */}
        <line x1="0" y1={midY} x2={width} y2={midY} stroke="rgba(184,137,74,0.3)" strokeWidth="0.7" strokeDasharray="3 5" />
        {/* today marker */}
        <line x1={px(0)} y1="0" x2={px(0)} y2={height} stroke="rgba(212,162,76,0.6)" strokeWidth="1" />
        <text x={px(0) + 4} y={12} fill="#d4a24c" fontSize="10" fontFamily="Georgia, serif" fontStyle="italic">today</text>
        {/* curves */}
        {data.points.map((c) => {
          const d = c.pts
            .map((p, i) => `${i === 0 ? "M" : "L"}${px(p.x).toFixed(2)},${py(p.y).toFixed(2)}`)
            .join(" ");
          return (
            <g key={c.name}>
              <path d={d} fill="none" stroke={c.colour} strokeWidth="1.7" opacity="0.9" />
              {/* dot at today */}
              <circle cx={px(0)} cy={py(valueAt(daysSince(dob), c.days))} r="3.5" fill={c.colour} />
            </g>
          );
        })}
      </svg>

      <div className="mt-4 pt-4 border-t border-[color:var(--gold)]/10 label-overline flex flex-wrap items-center gap-4">
        {CYCLES.map((c) => (
          <span key={c.name} className="inline-flex items-center gap-2">
            <span className="w-4 h-[3px]" style={{ background: c.colour }} />
            {c.name}
          </span>
        ))}
      </div>
    </div>
  );
}
